import React, { useState, useEffect } from 'react';
import {
	Document,
	DocumentType,
	useDocumentsQuery,
} from '../../../graphql/graphql';
import AddButton from '../../../Components/Button/AddButton';
import Text from '../../../Components/Base/Text/Text';
import MedicalReportDetailModal from '../../../Components/MedicalReport/MedicalReportDetailModal';
import { useAppContext } from '../../../Services/AppContext';
import styles from './MedicalReportsList.module.scss';
import { useFlatDataList } from '../../Base/Hooks/useFlatDataList';
import MedicalReportsList from './MedicalReportsList';

interface Props {
	patientId: string;
	workplaceId: string;
	caseId?: string;
}

const wrapperId = 'medicalReportsWrapper';

const MedicalReportsListWrapper: React.FC<Props> = ({
	patientId,
	workplaceId,
	caseId,
}) => {
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const { userData } = useAppContext();

	const { flatData, pagination, getNextPage, appendData, updateItem, reset } =
		useFlatDataList<Document>();

	const { data, isLoading, refetch, isFetching } = useDocumentsQuery(
		{
			userId: patientId,
			workplaceId,
			types: [DocumentType.MedicalReport],
			...pagination,
		},
		{ refetchOnMountOrArgChange: true },
	);

	useEffect(() => {
		appendData((data?.documents?.data as Document[]) ?? []);
	}, [data]);

	const refresh = async () => {
		reset();
		if (pagination.skip === 0) {
			const { data: refreshed } = await refetch();
			appendData((refreshed?.documents?.data as Document[]) ?? [], true);
		}
	};

	const handleOnCreate = () => {
		setIsCreateOpen(false);
		refresh();
	};

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.options}>
					<AddButton
						title='base.shared.createMedicalReport'
						onClick={() => setIsCreateOpen(true)}
					/>
				</div>
				<div className={styles.header}>
					<Text
						className={styles.date}
						tx='component.medicalReportList.header.date'
					/>
					<Text
						className={styles.document}
						tx='component.medicalReportList.header.document'
					/>
				</div>
				<MedicalReportsList
					data={flatData}
					isLoading={isLoading || isFetching}
					onRefresh={refresh}
					onLoadMore={getNextPage}
					hasMoreData={data?.documents?.meta?.hasNextPage || false}
				/>
			</div>
			{isCreateOpen && (
				<MedicalReportDetailModal
					opened
					onClose={handleOnCreate}
					workplaceId={workplaceId}
					patientId={patientId}
					loggedUserId={userData.id}
					caseId={caseId}
				/>
			)}
		</>
	);
};

export default MedicalReportsListWrapper;
